import { useEffect, useRef, useState } from 'react';

import {
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCircle2,
  ChevronRight,
  Info,
  Keyboard,
  Loader2,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  formatCombo,
  type ShortcutDefinition,
  useRegisterShortcut,
  useShortcutHelp,
} from '@erp-core/shortcuts';

import { type Notification, useUIStore } from '@stores/ui-store';

import {
  fetchNotificationsList,
  markNotificationsAsRead,
  type NotificationItem,
} from '@shared/services/notifications.api';
import { cn } from '@shared/utils/cn';

import styles from './ERPUtilityPanel.module.scss';

// ─── Notification icon helper ─────────────────────────────────────────────────

function NotifIcon({ variant }: { variant: Notification['variant'] }) {
  const props = { size: 14, 'aria-hidden': true };
  switch (variant) {
    case 'success':
      return <CheckCircle2 {...props} className={styles.notifIconSuccess} />;
    case 'error':
      return <AlertCircle {...props} className={styles.notifIconError} />;
    case 'warning':
      return <AlertTriangle {...props} className={styles.notifIconWarning} />;
    default:
      return <Info {...props} className={styles.notifIconInfo} />;
  }
}

function timeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  if (diff < 60_000) return 'just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  return `${Math.floor(diff / 86400_000)}d ago`;
}

function formatNotifTime(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getBookingIdFromNotif(n: NotificationItem): string | number | null {
  if (n.ref_data && typeof n.ref_data === 'object') {
    const ref = n.ref_data as Record<string, unknown>;
    const id = ref.booking_id ?? ref.bookingId ?? ref.id;
    if (id !== undefined && id !== null && id !== '') {
      return id as string | number;
    }
  }
  if (n.message) {
    const match = /#?(\d{3,7})/.exec(n.message);
    if (match?.[1]) return match[1];
  }
  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ERPUtilityPanel() {
  const navigate = useNavigate();

  const isOpen = useUIStore((s) => s.utilityPanelOpen);
  const activeTab = useUIStore((s) => s.utilityPanelTab);
  const closePanel = useUIStore((s) => s.closeUtilityPanel);
  const openPanel = useUIStore((s) => s.openUtilityPanel);
  const notifications = useUIStore((s) => s.notifications);
  const removeNotif = useUIStore((s) => s.removeNotification);

  const [serverNotifications, setServerNotifications] = useState<NotificationItem[]>([]);
  const [isLoadingNotifs, setIsLoadingNotifs] = useState(false);
  const [notifError, setNotifError] = useState<string | null>(null);

  const shortcuts = useShortcutHelp();

  const panelRef = useRef<HTMLDivElement>(null);
  const firstFocusRef = useRef<HTMLButtonElement>(null);

  // ── Fetch notifications & mark as read when panel is open ───────────────────
  useEffect(() => {
    if (!isOpen || activeTab !== 'notifications') return;

    let isMounted = true;

    const loadNotifs = async () => {
      setIsLoadingNotifs(true);
      setNotifError(null);
      try {
        const res = await fetchNotificationsList();
        if (!isMounted) return;
        setServerNotifications(res.notifications || []);
        setIsLoadingNotifs(false);

        // Mark as read on backend and clear header unread count
        await markNotificationsAsRead();
        useUIStore.getState().setUnreadNotificationsCount(0);
      } catch (err: unknown) {
        if (!isMounted) return;
        console.error('Failed to fetch notifications:', err);
        setNotifError('Unable to load notifications');
        setIsLoadingNotifs(false);
      }
    };

    void loadNotifs();

    return () => {
      isMounted = false;
    };
  }, [isOpen, activeTab]);

  // ── F1 toggles the shortcuts tab ───────────────────────────────────────────
  useRegisterShortcut({
    id: 'layout:shortcuts-help',
    combo: { key: 'F1' },
    scope: 'global',
    handler: () => openPanel('shortcuts'),
    meta: { label: 'Keyboard shortcuts', group: 'Layout', visible: true },
  });

  // ── Focus management ────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => firstFocusRef.current?.focus());
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closePanel();
      }
    }
    document.addEventListener('keydown', onKey, { capture: true });
    return () => document.removeEventListener('keydown', onKey, { capture: true });
  }, [isOpen, closePanel]);

  // Group shortcuts by meta.group
  const shortcutGroups = shortcuts.reduce<Record<string, ShortcutDefinition[]>>((acc, s) => {
    const g = s.meta?.group ?? 'General';
    if (!acc[g]) acc[g] = [];
    acc[g].push(s);
    return acc;
  }, {});

  const totalNotifCount = serverNotifications.length + notifications.length;

  const handleServerNotifClick = (n: NotificationItem) => {
    const bookingId = getBookingIdFromNotif(n);
    if (bookingId) {
      closePanel();
      navigate(`/booking/${bookingId}`);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && <div className={styles.backdrop} onClick={closePanel} aria-hidden="true" />}

      <aside
        ref={panelRef}
        className={cn(styles.panel, isOpen && styles.panelOpen)}
        aria-label="Quick panel"
        aria-hidden={!isOpen}
        id="erp-utility-panel"
      >
        {/* ── Panel header ─────────────────────────────────────────────────── */}
        <div className={styles.panelHeader}>
          {/* Tabs */}
          <div className={styles.tabs} role="tablist" aria-label="Panel sections">
            {(
              [
                { id: 'notifications', label: 'Notif.', icon: Bell },
                { id: 'shortcuts', label: 'Keys', icon: Keyboard },
              ] as const
            ).map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  ref={tab.id === activeTab ? firstFocusRef : undefined}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={cn(styles.tab, activeTab === tab.id && styles.tabActive)}
                  onClick={() => openPanel(tab.id)}
                >
                  <Icon size={13} aria-hidden="true" />
                  <span>{tab.label}</span>
                  {tab.id === 'notifications' && totalNotifCount > 0 && (
                    <span className={styles.tabBadge}>{totalNotifCount}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Close button */}
          <button
            type="button"
            className={styles.closeBtn}
            onClick={closePanel}
            aria-label="Close panel"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>

        {/* ── Notifications tab ────────────────────────────────────────────── */}
        {activeTab === 'notifications' && (
          <div role="tabpanel" aria-label="Notifications" className={styles.tabPanel}>
            {isLoadingNotifs ? (
              <div className={styles.empty}>
                <Loader2 size={24} className={styles.spinning} aria-hidden="true" />
                <p className={styles.emptyText}>Loading notifications...</p>
              </div>
            ) : notifError ? (
              <div className={styles.empty}>
                <p className={styles.emptyText}>{notifError}</p>
              </div>
            ) : totalNotifCount === 0 ? (
              <div className={styles.empty}>
                <Bell size={28} className={styles.emptyIcon} aria-hidden="true" />
                <p className={styles.emptyText}>No notifications</p>
              </div>
            ) : (
              <ul className={styles.notifList}>
                {serverNotifications.map((n) => {
                  const bookingId = getBookingIdFromNotif(n);
                  const isClickable = Boolean(bookingId);
                  return (
                    <li
                      key={`server-${n.id}`}
                      className={cn(styles.notifItem, styles['notif-info'])}
                    >
                      {isClickable ? (
                        <button
                          type="button"
                          className={cn(styles.notifBtn, styles.notifClickable)}
                          onClick={() => handleServerNotifClick(n)}
                        >
                          <div className={styles.notifIconWrap}>
                            <NotifIcon variant="info" />
                          </div>
                          <div className={styles.notifBody}>
                            {n.title && <div className={styles.notifTitle}>{n.title}</div>}
                            <div className={styles.notifMessage}>{n.message}</div>
                            <div className={styles.notifTime}>{formatNotifTime(n.created_at)}</div>
                          </div>
                          <div className={styles.notifArrow}>
                            <ChevronRight size={14} aria-hidden="true" />
                          </div>
                        </button>
                      ) : (
                        <div className={styles.notifContent}>
                          <div className={styles.notifIconWrap}>
                            <NotifIcon variant="info" />
                          </div>
                          <div className={styles.notifBody}>
                            {n.title && <div className={styles.notifTitle}>{n.title}</div>}
                            <div className={styles.notifMessage}>{n.message}</div>
                            <div className={styles.notifTime}>{formatNotifTime(n.created_at)}</div>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
                {notifications.map((n) => (
                  <li key={n.id} className={cn(styles.notifItem, styles[`notif-${n.variant}`])}>
                    <div className={styles.notifContent}>
                      <div className={styles.notifIconWrap}>
                        <NotifIcon variant={n.variant} />
                      </div>
                      <div className={styles.notifBody}>
                        {n.title && <div className={styles.notifTitle}>{n.title}</div>}
                        <div className={styles.notifMessage}>{n.message}</div>
                        <div className={styles.notifTime}>{timeAgo(n.createdAt)}</div>
                      </div>
                      <button
                        type="button"
                        className={styles.notifClose}
                        onClick={() => removeNotif(n.id)}
                        aria-label={`Dismiss: ${n.message}`}
                      >
                        <X size={11} aria-hidden="true" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ── Shortcuts tab ────────────────────────────────────────────────── */}
        {activeTab === 'shortcuts' && (
          <div role="tabpanel" aria-label="Keyboard shortcuts" className={styles.tabPanel}>
            {Object.keys(shortcutGroups).length === 0 ? (
              <div className={styles.empty}>
                <Keyboard size={28} className={styles.emptyIcon} aria-hidden="true" />
                <p className={styles.emptyText}>No shortcuts registered</p>
              </div>
            ) : (
              Object.entries(shortcutGroups).map(([group, items]) => (
                <div key={group} className={styles.shortcutGroup}>
                  <div className={styles.shortcutGroupLabel}>{group}</div>
                  <ul className={styles.shortcutList}>
                    {items.map((sc) => (
                      <li key={sc.id} className={styles.shortcutItem}>
                        <span className={styles.shortcutLabel}>{sc.meta?.label ?? sc.id}</span>
                        <kbd className={styles.shortcutKbd}>{formatCombo(sc.combo)}</kbd>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        )}
      </aside>
    </>
  );
}
